import { getDbSupabase } from './supabase';

/** Item row from purchasing.item_list via certificate-request-api. */
export type CertificateItemLookup = {
  code: string;
  description: string;
  /** item_list.material — e.g. PAPER PULP / PS PLASTIC */
  material: string;
  /** item_list.vendor_no */
  vendor_no: string;
  uom: string;
  /** item_list.std_ctn_packing */
  packing: string;
  /** item_list.ctn_measurement */
  ctn_measurement: string;
};

type CertificateItemsResponse = {
  items?: Array<{
    code?: string;
    description?: string;
    material?: string;
    vendor_no?: string;
    uom?: string;
    packing?: string;
    ctn_measurement?: string;
  }>;
  error?: string;
};

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('forms').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }
  return headers;
}

/** Search PowerBI purchasing.item_list for Certificate Request lines. */
export async function searchCertificateItems(
  query: string,
  limit = 40,
): Promise<CertificateItemLookup[]> {
  const q = query.trim();
  if (!q) return [];

  const headers = await authHeaders();
  const params = new URLSearchParams({
    resource: 'items',
    q,
    limit: String(Math.min(Math.max(limit, 1), 50)),
  });
  const response = await fetch(`/certificate-request-api?${params.toString()}`, { headers });
  const json = (await response.json().catch(() => null)) as CertificateItemsResponse | null;
  if (!response.ok) {
    throw new Error(json?.error || `Item search failed (${response.status}).`);
  }

  return (json?.items ?? [])
    .map((row) => {
      const code = String(row.code ?? '').trim();
      if (!code) return null;
      return {
        code,
        description: String(row.description ?? '').trim(),
        material: String(row.material ?? '').trim(),
        vendor_no: String(row.vendor_no ?? '').trim(),
        uom: String(row.uom ?? '').trim(),
        packing: String(row.packing ?? '').trim(),
        ctn_measurement: String(row.ctn_measurement ?? '').trim(),
      } satisfies CertificateItemLookup;
    })
    .filter((row): row is CertificateItemLookup => Boolean(row));
}

export type CertificateItemCertMeta = {
  code: string;
  material: string;
  vendor_no: string;
};

type CertificateItemMetaResponse = {
  items?: Array<{ code?: string; material?: string; vendor_no?: string }>;
  error?: string;
};

/** Load vendor + material for all item_list rows (for certificate card badges). */
export async function loadCertificateItemCertMetaMap(): Promise<
  Map<string, CertificateItemCertMeta>
> {
  const headers = await authHeaders();
  const params = new URLSearchParams({ resource: 'item_cert_meta' });
  const response = await fetch(`/certificate-request-api?${params.toString()}`, { headers });
  const json = (await response.json().catch(() => null)) as CertificateItemMetaResponse | null;
  if (!response.ok) {
    throw new Error(json?.error || `Item cert meta failed (${response.status}).`);
  }

  const map = new Map<string, CertificateItemCertMeta>();
  for (const row of json?.items ?? []) {
    const code = String(row.code ?? '').trim();
    if (!code) continue;
    const meta: CertificateItemCertMeta = {
      code,
      material: String(row.material ?? '').trim(),
      vendor_no: String(row.vendor_no ?? '').trim(),
    };
    map.set(code.toUpperCase(), meta);
    const loose = normalizeItemCodeKey(code);
    if (loose && loose !== code.toUpperCase()) {
      map.set(loose, meta);
    }
  }
  return map;
}

/** Uppercase + strip spaces/hyphens so DEM codes match item_list nos. */
export function normalizeItemCodeKey(code: string): string {
  return code.trim().toUpperCase().replace(/[\s\-_./]+/g, '');
}

export function getCertificateItemCertMeta(
  map: Map<string, CertificateItemCertMeta>,
  itemCode: string,
): CertificateItemCertMeta | undefined {
  const raw = itemCode.trim();
  if (!raw) return undefined;
  return map.get(raw.toUpperCase()) ?? map.get(normalizeItemCodeKey(raw));
}
